import { useEffect } from 'react';

import { completeBootTrace } from './boot-trace';

export const BOOT_SPLASH_READY_EVENT = 'streamlain:app-shell-ready';
const BOOT_SPLASH_READY_GLOBAL = '__streamlainBootSplashReady';
const BOOT_SPLASH_FADE_MS = 750;
const BOOT_SPLASH_UNDERLAY_TIMEOUT_MS = 1200;
const BOOT_SPLASH_READY_FALLBACK_MS = 80;
const BOOT_SPLASH_UNDERLAY_POLL_MS = 50;

declare global {
  interface Window {
    __streamlainBootSplashReady?: boolean;
  }
}

function isBlueWhiteBootTheme() {
  return (
    document.documentElement.getAttribute('data-sl-boot-theme') ===
      'blue-white' || localStorage.getItem('theme') === 'blue-white'
  );
}

function parseRgb(color: string): [number, number, number] | null {
  const match = color.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
  if (!match) {
    return null;
  }

  return [Number(match[1]), Number(match[2]), Number(match[3])];
}

function isWhiteColor(color: string): boolean {
  const rgb = parseRgb(color);
  if (!rgb) {
    return false;
  }

  return rgb.every(channel => channel >= 245);
}

function readVisibleBackground(element: Element | null): string | null {
  let current: Element | null = element;
  while (current) {
    const background = getComputedStyle(current).backgroundColor;
    if (
      background &&
      background !== 'transparent' &&
      !background.endsWith(', 0)')
    ) {
      return background;
    }
    current = current.parentElement;
  }

  return getComputedStyle(document.body).backgroundColor;
}

function isBootUnderlayReady(): boolean {
  if (!isBlueWhiteBootTheme()) {
    return true;
  }

  const element = document.elementFromPoint(
    window.innerWidth / 2,
    window.innerHeight / 2
  );
  const background = readVisibleBackground(element);
  return background ? !isWhiteColor(background) : false;
}

function waitForBootUnderlayReady(callback: () => void) {
  const startedAt = performance.now();
  let timeout = 0;
  let done = false;

  const tick = () => {
    if (done) {
      return;
    }

    if (
      isBootUnderlayReady() ||
      performance.now() - startedAt >= BOOT_SPLASH_UNDERLAY_TIMEOUT_MS
    ) {
      done = true;
      callback();
      return;
    }
    timeout = window.setTimeout(tick, BOOT_SPLASH_UNDERLAY_POLL_MS);
  };

  tick();

  return () => {
    done = true;
    window.clearTimeout(timeout);
  };
}

function scheduleAfterNextPaint(callback: () => void) {
  let firstFrame = 0;
  let secondFrame = 0;
  let fallback = 0;
  let done = false;

  const run = () => {
    if (done) {
      return;
    }

    done = true;
    callback();
  };

  firstFrame = window.requestAnimationFrame(() => {
    secondFrame = window.requestAnimationFrame(run);
  });
  fallback = window.setTimeout(run, BOOT_SPLASH_READY_FALLBACK_MS);

  return () => {
    done = true;
    window.cancelAnimationFrame(firstFrame);
    window.cancelAnimationFrame(secondFrame);
    window.clearTimeout(fallback);
  };
}

function dismissBootSplash() {
  const splash = document.getElementById('sl-boot-splash');
  if (!splash) {
    return;
  }

  return waitForBootUnderlayReady(() => {
    splash.setAttribute('data-state', 'fade');
    window.setTimeout(() => {
      splash.remove();
      document.documentElement.removeAttribute('data-sl-boot-theme');
    }, BOOT_SPLASH_FADE_MS + 40);
  });
}

function scheduleBootSplashDismiss() {
  let cancelUnderlayWait: (() => void) | undefined;
  const cancelPaintWait = scheduleAfterNextPaint(() => {
    cancelUnderlayWait = dismissBootSplash();
  });

  return () => {
    cancelPaintWait();
    cancelUnderlayWait?.();
  };
}

export function dispatchBootSplashReady() {
  completeBootTrace({ signal: 'app-shell-ready' });
  window[BOOT_SPLASH_READY_GLOBAL] = true;
  window.dispatchEvent(new CustomEvent(BOOT_SPLASH_READY_EVENT));
  scheduleBootSplashDismiss();
}

function hasBootSplashReady() {
  return window[BOOT_SPLASH_READY_GLOBAL] === true;
}

export function useBootSplashReady(ready = true) {
  useEffect(() => {
    if (!ready) {
      return;
    }

    return scheduleAfterNextPaint(dispatchBootSplashReady);
  }, [ready]);
}

export function BootSplashDismissListener({
  readyOnMount = false,
}: {
  readyOnMount?: boolean;
}) {
  useEffect(() => {
    let cancelDismiss: (() => void) | undefined;
    const onReady = () => {
      cancelDismiss?.();
      cancelDismiss = scheduleBootSplashDismiss();
    };

    if (readyOnMount || hasBootSplashReady()) {
      onReady();
      return () => {
        cancelDismiss?.();
      };
    }

    window.addEventListener(BOOT_SPLASH_READY_EVENT, onReady, { once: true });
    return () => {
      window.removeEventListener(BOOT_SPLASH_READY_EVENT, onReady);
      cancelDismiss?.();
    };
  }, [readyOnMount]);

  return null;
}
